import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Plus, Trash2, RefreshCw, Pencil, Eye, EyeOff } from "lucide-react";

const FY_MONTHS = ["Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb"];
const fmt = (n, d = 2) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });

// Distinct line colours for up to 12 cost items
const LINE_COLOURS = [
  "hsl(20, 39%, 39%)",   // primary terracotta
  "hsl(31, 65%, 53%)",   // amber
  "hsl(132, 18%, 36%)",  // sage
  "hsl(220, 60%, 45%)",  // blue
  "hsl(0, 60%, 50%)",    // red
  "hsl(280, 35%, 45%)",  // purple
  "hsl(40, 80%, 40%)",   // gold
  "hsl(180, 35%, 35%)",  // teal
  "hsl(330, 50%, 50%)",  // pink
  "hsl(60, 50%, 35%)",   // olive
  "hsl(245, 30%, 50%)",  // indigo
  "hsl(15, 65%, 50%)",   // orange
];

function fyForToday() {
  const t = new Date();
  return t.getMonth() + 1 >= 3 ? t.getFullYear() + 1 : t.getFullYear();
}

// month_label "YYYY-MM" → short label "Mar 26"
function shortLabel(k) {
  const [y, m] = k.split("-").map(Number);
  return `${FY_MONTHS[((m + 9) % 12)]} ${String(y).slice(-2)}`;
}

export default function MonthlyCosts() {
  const [fy, setFy] = useState(fyForToday());
  const [trend, setTrend] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [hidden, setHidden] = useState({});         // {itemId: true} = hide line
  const [yAxisMode, setYAxisMode] = useState("rand_per_kg");  // 'rand_per_kg' | 'amount'
  const [showManager, setShowManager] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const load = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.get(`/cost-items/fy-trend?fy=${fy}`);
      setTrend(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fy]);

  // Sync each FY month from Zoho (loops through, calling /zoho/account-totals?refresh=true)
  const syncFy = async () => {
    if (!trend) return;
    setSyncBusy(true); setSyncMsg("");
    try {
      let done = 0;
      for (const k of trend.months) {
        const [y, m] = k.split("-").map(Number);
        try {
          await api.get(`/zoho/account-totals?year=${y}&month=${m}&refresh=true`);
        } catch { /* tolerate one-off failures */ }
        done += 1;
        setSyncMsg(`Synced ${done} / ${trend.months.length} months…`);
      }
      setSyncMsg("FY sync complete");
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSyncBusy(false); }
  };

  const visibleItems = (trend?.items || []).filter((it) => !hidden[it.id]);
  const chartData = useMemo(() => {
    if (!trend) return [];
    return trend.months.map((k, idx) => {
      const row = { x: shortLabel(k), kg: trend.kg_per_month[idx] };
      for (const it of trend.items) {
        if (hidden[it.id]) continue;
        row[it.name] = yAxisMode === "amount" ? it.amounts[idx] : it.rand_per_kg[idx];
      }
      return row;
    });
  }, [trend, hidden, yAxisMode]);

  const toggleAll = (val) => {
    if (!trend) return;
    const next = {};
    for (const it of trend.items) next[it.id] = val;
    setHidden(next);
  };

  return (
    <div data-testid="monthly-costs-page">
      <PageHeader
        eyebrow="Costing"
        title="Monthly Costs"
        description="Configurable cost items linked to Zoho Books expense accounts. Each cost is auto-divided by the kg sold that month → Rand-per-kg. Track trend movement across the financial year and toggle which items appear on the chart."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setShowManager({ mode: "new" })} data-testid="add-cost-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">
              <Plus className="w-4 h-4" /> Add cost
            </button>
            <button onClick={syncFy} disabled={syncBusy} data-testid="sync-fy-btn"
              className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded text-sm hover:bg-secondary disabled:opacity-60">
              <RefreshCw className={`w-4 h-4 ${syncBusy ? "animate-spin" : ""}`} />
              {syncBusy ? "Syncing…" : "Sync FY from Zoho"}
            </button>
          </div>
        }
      />

      {/* FY picker + sync state */}
      <div className="bg-card border border-border rounded p-4 mb-5 flex flex-wrap items-center gap-3">
        <label className="label-tiny">Financial year</label>
        <select value={fy} onChange={(e) => setFy(parseInt(e.target.value))}
          data-testid="fy-picker"
          className="px-3 py-1.5 bg-white border border-border rounded text-sm font-mono">
          {[fyForToday() + 1, fyForToday(), fyForToday() - 1, fyForToday() - 2].map((y) => (
            <option key={y} value={y}>FY {y} (Mar {y - 1} – Feb {y})</option>
          ))}
        </select>
        {trend && (
          <span className="text-xs text-muted-foreground font-mono ml-auto" data-testid="fy-sync-status">
            {trend.synced_months.length} / 12 months pulled from Zoho
          </span>
        )}
        {syncMsg && <span className="text-xs text-[hsl(132,18%,36%)] font-mono">{syncMsg}</span>}
      </div>

      {error && <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm mb-4" data-testid="costs-error">{error}</div>}

      {busy && !trend && <p className="text-sm text-muted-foreground py-12 text-center">Loading FY trend…</p>}

      {trend && trend.items.length === 0 && (
        <div className="bg-card border border-border rounded p-12 text-center">
          <p className="text-sm text-muted-foreground mb-4">No cost items configured yet.</p>
          <button onClick={() => setShowManager({ mode: "new" })}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">
            <Plus className="w-4 h-4" /> Add your first cost
          </button>
        </div>
      )}

      {trend && trend.items.length > 0 && (
        <>
          {/* Chart card */}
          <div className="bg-card border border-border rounded p-5 mb-5" data-testid="fy-trend-chart">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h3 className="font-display text-lg font-bold">FY {fy} cost trend</h3>
              <div className="inline-flex border border-border rounded overflow-hidden text-xs font-semibold">
                <button onClick={() => setYAxisMode("rand_per_kg")} data-testid="chart-mode-rpkg"
                  className={`px-3 py-1.5 ${yAxisMode === "rand_per_kg" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-secondary"}`}>
                  R / kg
                </button>
                <button onClick={() => setYAxisMode("amount")} data-testid="chart-mode-amount"
                  className={`px-3 py-1.5 border-l border-border ${yAxisMode === "amount" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-secondary"}`}>
                  R amount
                </button>
              </div>
            </div>

            <div style={{ width: "100%", height: 360 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(40 21% 87%)" />
                  <XAxis dataKey="x" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }}
                    tickFormatter={(v) => yAxisMode === "amount" ? `R${(v / 1000).toFixed(0)}k` : `R${v.toFixed(2)}`} />
                  <Tooltip
                    formatter={(v, name) => [yAxisMode === "amount" ? `R ${fmt(v)}` : `R ${fmt(v, 4)}/kg`, name]}
                    labelFormatter={(l) => l}
                    contentStyle={{ background: "hsl(40 21% 96%)", border: "1px solid hsl(40 21% 87%)", borderRadius: 4, fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {visibleItems.map((it, i) => (
                    <Line
                      key={it.id}
                      type="monotone"
                      dataKey={it.name}
                      stroke={LINE_COLOURS[i % LINE_COLOURS.length]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Toggles */}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center justify-between mb-2">
                <p className="label-tiny">Toggle items on the chart</p>
                <div className="flex gap-1 text-xs">
                  <button onClick={() => toggleAll(false)} data-testid="toggle-show-all"
                    className="px-2 py-0.5 border border-border rounded hover:bg-secondary">Show all</button>
                  <button onClick={() => toggleAll(true)} data-testid="toggle-hide-all"
                    className="px-2 py-0.5 border border-border rounded hover:bg-secondary">Hide all</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {trend.items.map((it, i) => {
                  const off = !!hidden[it.id];
                  const colour = LINE_COLOURS[trend.items.indexOf(it) % LINE_COLOURS.length];
                  return (
                    <button key={it.id}
                      onClick={() => setHidden({ ...hidden, [it.id]: !off })}
                      data-testid={`toggle-${it.id}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold transition ${
                        off ? "bg-muted text-muted-foreground border-border line-through opacity-70" : "bg-card border-border hover:bg-secondary"
                      }`}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ background: off ? "transparent" : colour, border: `1px solid ${colour}` }} />
                      {off ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      {it.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* FY table: cost items × months */}
          <div className="bg-card border border-border rounded overflow-hidden mb-5">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="fy-trend-table">
                <thead className="bg-secondary">
                  <tr>
                    <th className="text-left p-2 label-tiny sticky left-0 bg-secondary z-10 min-w-[180px]">Cost item</th>
                    {trend.months.map((k) => (
                      <th key={k} className="text-right p-2 label-tiny min-w-[90px]">{shortLabel(k)}</th>
                    ))}
                    <th className="text-right p-2 label-tiny min-w-[100px] bg-primary/10">FY total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-secondary/40 border-b border-border">
                    <td className="p-2 font-mono text-[10px] text-muted-foreground sticky left-0 bg-secondary/40">kg sold</td>
                    {trend.kg_per_month.map((kg, i) => (
                      <td key={i} className="p-2 text-right font-mono text-[10px] text-muted-foreground">{fmt(kg, 1)}</td>
                    ))}
                    <td className="p-2 text-right font-mono text-[10px] font-semibold bg-primary/5">
                      {fmt(trend.kg_per_month.reduce((s, x) => s + x, 0), 1)}
                    </td>
                  </tr>
                  {trend.items.map((it) => (
                    <tr key={it.id} className="border-t border-border hover:bg-secondary/40 cursor-pointer"
                      onClick={() => setShowManager({ mode: "edit", id: it.id })}
                      data-testid={`cost-row-${it.id}`}>
                      <td className="p-2 font-medium sticky left-0 bg-card hover:bg-secondary/40">
                        <p>{it.name}</p>
                        {it.account_code && (
                          <p className="text-[10px] text-muted-foreground font-mono">{it.account_code}{it.account_name ? ` · ${it.account_name}` : ""}</p>
                        )}
                      </td>
                      {it.amounts.map((amt, i) => {
                        const rpkg = it.rand_per_kg[i];
                        return (
                          <td key={i} className="p-2 text-right font-mono">
                            {rpkg > 0 ? (
                              <>
                                <p className="text-primary font-semibold">R {fmt(rpkg, 2)}/kg</p>
                                <p className="text-[10px] text-muted-foreground">R {fmt(amt, 0)}</p>
                              </>
                            ) : amt > 0 ? (
                              <>
                                <p className="text-muted-foreground">—</p>
                                <p className="text-[10px] text-muted-foreground">R {fmt(amt, 0)} <span className="italic">(no kg)</span></p>
                              </>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="p-2 text-right font-mono font-semibold bg-primary/5">R {fmt(it.fy_total, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-[11px] text-muted-foreground bg-secondary/40 border border-border rounded p-3">
            Click any row to edit. Cost amounts are auto-pulled from Zoho — if a cell shows "—", that month hasn't been synced yet (use <em>Sync FY from Zoho</em>).
          </div>
        </>
      )}

      <PackagingCostsSection />

      {showManager && (
        <CostItemEditor
          mode={showManager.mode}
          itemId={showManager.id}
          onClose={() => setShowManager(false)}
          onSaved={async () => { setShowManager(false); await load(); }}
        />
      )}
    </div>
  );
}

// ---- Cost item editor (single modal, replaces prior CostItemsManager flow) ----

const MFG_KEYS = [
  ["", "— None (reference only)"],
  ["gas", "Gas (feeds Mfg Cost)"],
  ["cargo_delivery", "Cargo / Freight"],
  ["postage_delivery", "Postage / Delivery"],
  ["fuel", "Fuel"],
  ["coffee_packaging", "Coffee Packaging (ref)"],
  ["packaging_material", "Packaging Material (ref)"],
];

function CostItemEditor({ mode, itemId, onClose, onSaved }) {
  const [accounts, setAccounts] = useState([]);
  const [item, setItem] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const accReq = api.get("/zoho/accounts?only_expense=true");
        if (mode === "edit" && itemId) {
          const [a, items] = await Promise.all([accReq, api.get("/cost-items")]);
          setAccounts(a.data);
          setItem(items.data.find((x) => x.id === itemId) || blank());
        } else {
          const a = await accReq;
          setAccounts(a.data);
          const items = await api.get("/cost-items");
          setItem(blank((items.data.at(-1)?.sort_order || 0) + 10));
        }
      } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    })();
  }, [mode, itemId]);

  const onPickAccount = (acctId) => {
    const a = accounts.find((x) => x.account_id === acctId);
    setItem({
      ...item,
      account_id: a?.account_id || "",
      account_code: a?.account_code || "",
      account_name: a?.account_name || "",
    });
  };

  const save = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const payload = {
        name: item.name,
        account_code: item.account_code || null,
        account_id: item.account_id || null,
        account_name: item.account_name || null,
        key: item.key || null,
        sort_order: parseInt(item.sort_order) || 0,
        is_active: !!item.is_active,
        notes: item.notes || "",
      };
      if (item.id) await api.put(`/cost-items/${item.id}`, payload);
      else         await api.post("/cost-items", payload);
      await onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!item?.id) return;
    if (!window.confirm("Delete this cost item? Historical values pulled from Zoho will no longer appear in the FY trend.")) return;
    try {
      await api.delete(`/cost-items/${item.id}`);
      await onSaved();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
  };

  if (!item) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
        <div className="bg-card border border-border rounded shadow-xl p-8 text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4" data-testid="cost-editor">
      <div className="bg-card border border-border rounded shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card z-10">
          <h3 className="font-display text-lg font-bold">{item.id ? "Edit cost" : "Add cost"}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-lg">✕</button>
        </div>
        <form onSubmit={save} className="p-5 space-y-3 text-sm">
          {error && <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-xs">{error}</div>}
          <CIField label="Cost name" required>
            <input type="text" required value={item.name} data-testid="cost-name"
              onChange={(e) => setItem({ ...item, name: e.target.value })}
              placeholder="e.g. Roaster Gas Propane"
              className="w-full px-3 py-2 border border-border rounded" />
          </CIField>
          <CIField label="Zoho expense category" required>
            <select value={item.account_id || ""} onChange={(e) => onPickAccount(e.target.value)}
              data-testid="cost-account" required
              className="w-full px-3 py-2 border border-border rounded text-sm">
              <option value="">— select expense account —</option>
              {accounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>
                  {a.account_code ? `${a.account_code} · ` : ""}{a.account_name}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-muted-foreground mt-1">
              Cost amounts come straight from this Zoho account on the P&amp;L for each month.
            </p>
          </CIField>
          <CIField label="Feed into Manufacturing Cost">
            <select value={item.key || ""} onChange={(e) => setItem({ ...item, key: e.target.value })}
              data-testid="cost-key"
              className="w-full px-3 py-2 border border-border rounded text-sm">
              {MFG_KEYS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <p className="text-[10px] text-muted-foreground mt-1">
              Tells the Manufacturing Cost calculation which bucket this item should
              feed into (Gas / Freight / etc). Leave on <em>None</em> if you only want
              to track this cost on the FY trend chart without having it flow into the
              per-blend Rand-per-kg breakdown.
            </p>
          </CIField>
          <CIField label="Active">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={!!item.is_active}
                onChange={(e) => setItem({ ...item, is_active: e.target.checked })}
                className="accent-[hsl(20,39%,39%)]" />
              <span className="text-sm">{item.is_active ? "Active" : "Inactive"}</span>
            </label>
            <p className="text-[10px] text-muted-foreground mt-1">
              Inactive items are hidden from the FY trend chart but their history is preserved.
            </p>
          </CIField>
          <CIField label="Notes">
            <textarea rows="2" value={item.notes || ""} onChange={(e) => setItem({ ...item, notes: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded text-xs" />
          </CIField>
          <div className="flex gap-2 justify-between pt-2 border-t border-border">
            <div>
              {item.id && (
                <button type="button" onClick={remove} data-testid="cost-delete"
                  className="inline-flex items-center gap-1 px-3 py-2 text-xs text-destructive border border-destructive/30 rounded hover:bg-destructive/10">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
              <button type="submit" disabled={busy} data-testid="cost-save"
                className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60">
                <Pencil className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

const blank = (sortOrder = 10) => ({
  id: null, name: "", account_id: "", account_code: "", account_name: "",
  key: "", sort_order: sortOrder, is_active: true, notes: "",
});

function CIField({ label, required, children }) {
  return (
    <div>
      <p className="label-tiny mb-1">{label}{required && <span className="text-destructive ml-0.5">*</span>}</p>
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Packaging items (kept — feeds item-mapping packaging cost)
// -----------------------------------------------------------------------------
function PackagingCostsSection() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/packaging-items");
      setRows(data);
    } catch { setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const save = async (row) => {
    setBusy(true); setError("");
    try {
      const payload = {
        name: row.name,
        unit_cost: parseFloat(row.unit_cost) || 0,
        sort_order: row.sort_order || 0,
        is_active: row.is_active !== false,
        notes: row.notes || "",
      };
      if (row.id) await api.put(`/packaging-items/${row.id}`, payload);
      else        await api.post("/packaging-items", payload);
      setAdding(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this packaging item?")) return;
    await api.delete(`/packaging-items/${id}`);
    await load();
  };

  return (
    <div className="bg-card border border-border rounded p-5 mt-8" data-testid="packaging-costs-section">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display text-lg font-bold">Packaging items (per-unit)</h3>
        <button onClick={() => setAdding({ name: "", unit_cost: 0, sort_order: (rows.at(-1)?.sort_order || 0) + 10, is_active: true, notes: "" })}
          data-testid="pkg-new"
          className="px-3 py-1.5 text-xs border border-border rounded hover:bg-secondary">
          + Add packaging item
        </button>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Per-unit cost of pouches / labels / stickers. Each Zoho coffee item gets a list of packaging items in <em>Item Mapping</em> so the Manufacturing Cost tab can compute the correct R/kg.
      </p>
      {error && <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-xs mb-3">{error}</div>}
      <table className="w-full text-sm">
        <thead className="bg-secondary">
          <tr>
            <th className="text-left p-2 label-tiny">Item name</th>
            <th className="text-right p-2 label-tiny">Unit cost (R)</th>
            <th className="text-left p-2 label-tiny">Notes</th>
            <th className="text-left p-2 label-tiny">Status</th>
            <th className="text-right p-2 label-tiny w-32">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <PackagingRow key={r.id} row={r} onSave={save} onDelete={remove} busy={busy} />
          ))}
          {adding && <PackagingRow row={adding} onSave={save} busy={busy} isNew onCancel={() => setAdding(null)} />}
        </tbody>
      </table>
    </div>
  );
}

function PackagingRow({ row, onSave, onDelete, busy, isNew, onCancel }) {
  const [name, setName] = useState(row.name ?? "");
  const [cost, setCost] = useState(row.unit_cost ?? 0);
  const [active, setActive] = useState(row.is_active !== false);
  const dirty = isNew || name !== row.name || parseFloat(cost) !== row.unit_cost || active !== row.is_active;
  const slug = (row.id || row.name || "new").toString().slice(0, 20).replace(/\s+/g, "-").toLowerCase();
  return (
    <tr className="border-t border-border" data-testid={`pkg-row-${slug}`}>
      <td className="p-2">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 1kg Pouch"
          data-testid={`pkg-name-${slug}`}
          className="w-full px-2 py-1 border border-border rounded text-sm font-medium" />
      </td>
      <td className="p-2 text-right">
        <input type="number" step="0.01" min="0" value={cost} onChange={(e) => setCost(e.target.value)}
          data-testid={`pkg-cost-${slug}`}
          className="w-24 px-2 py-1 border border-border rounded font-mono text-right" />
      </td>
      <td className="p-2 text-xs text-muted-foreground">{row.notes || "—"}</td>
      <td className="p-2">
        <label className="inline-flex items-center gap-1 cursor-pointer text-xs">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)}
            className="accent-[hsl(20,39%,39%)]" />
          {active ? "active" : "inactive"}
        </label>
      </td>
      <td className="p-2 text-right">
        <div className="inline-flex gap-1">
          {dirty && name && (
            <button onClick={() => onSave({ ...row, name, unit_cost: cost, is_active: active })}
              disabled={busy} data-testid={`pkg-save-${slug}`}
              className="px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded">
              Save
            </button>
          )}
          {isNew && (
            <button onClick={onCancel} className="px-2 py-1 text-xs border border-border rounded hover:bg-secondary">
              Cancel
            </button>
          )}
          {!isNew && onDelete && (
            <button onClick={() => onDelete(row.id)} data-testid={`pkg-delete-${slug}`}
              className="p-1.5 text-destructive border border-destructive/30 rounded hover:bg-destructive/10">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
